import React from 'react';

import { Button, Icon, Stack, Text, Textarea } from '@eluve/components';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';

export interface InlineFeedbackProps {
  sectionLabel: string;
  feedbackType: FeedbackTypeEnum | null;
  isSubmitting: boolean;
  isFinalized: boolean;
  onThumbsClick: (type: FeedbackTypeEnum) => void;
  onCommentSubmit: (comment: string) => void;
  onClose?: () => void;
  justify?: 'start' | 'center' | 'end';
}

export const InlineFeedback: React.FC<InlineFeedbackProps> = ({
  sectionLabel,
  feedbackType,
  isSubmitting,
  isFinalized,
  onThumbsClick,
  onCommentSubmit,
  onClose,
  justify = 'end',
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = React.useState<string>('');
  const disableAll = isSubmitting || isFinalized;

  const handleThumbs = (type: FeedbackTypeEnum) => {
    if (disableAll) {
      return;
    }
    onThumbsClick(type);
  };

  const handleSubmitClick = () => {
    if (!feedbackType || isFinalized) {
      return;
    }
    onCommentSubmit(comment);
  };

  const handleCloseClick = () => {
    if (isFinalized) {
      return;
    }
    onClose && onClose();
    setComment('');
  };

  return (
    <Stack align="stretch" gap={2}>
      <Stack justify={justify} align="center" gap={2} direction="horizontal">
        <Text inline color="secondary" weight="regular">
          {sectionLabel}
        </Text>
        <Button
          size="sm"
          variant={feedbackType === 'POSITIVE' ? 'default' : 'outline'}
          disabled={disableAll}
          onClick={() => handleThumbs('POSITIVE')}
        >
          <Icon name="ThumbsUp" size="auto" />
          {t('appointment.feedback.yes')}
        </Button>
        <Button
          size="sm"
          variant={feedbackType === 'NEGATIVE' ? 'default' : 'outline'}
          disabled={disableAll}
          onClick={() => handleThumbs('NEGATIVE')}
        >
          <Icon name="ThumbsDown" size="auto" />
          {t('appointment.feedback.no')}
        </Button>
      </Stack>

      {feedbackType && !isFinalized && (
        <div className="relative rounded-md bg-bg-secondary">
          {onClose && (
            <Stack justify="end" gap={2} direction="horizontal">
              <button
                className="m-3 cursor-pointer"
                onClick={handleCloseClick}
                aria-label={t('appointment.feedback.closeForm')}
              >
                <Icon name="X" />
              </button>
            </Stack>
          )}
          <Stack className="px-3" gap={3}>
            <Text inline weight="bold" color="tertiary">
              {t('appointment.feedback.commentsOptional')}
            </Text>
            <Textarea
              className="w-full resize-y rounded-md text-text-primary outline-hidden focus-visible:ring-0"
              value={comment}
              placeholder={t('appointment.feedback.tellMore')}
              onChange={(e) => setComment(e.currentTarget.value)}
            />
            <Stack justify="end" className="p-3" gap={2} direction="horizontal">
              <Button
                size="sm"
                disabled={disableAll}
                onClick={handleSubmitClick}
              >
                {t('appointment.feedback.submitShort')}
              </Button>
            </Stack>
          </Stack>
        </div>
      )}
    </Stack>
  );
};
